"use client"

import type { Organization } from "better-auth/plugins/organization"
import { useContext, useMemo, useRef, useState } from "react"

import { useCurrentOrganization } from "../../hooks/use-current-organization"
import { AuthUIContext } from "../../lib/auth-ui-provider"
import type { SettingsCardProps } from "../settings/shared/settings-card"
import { SettingsCard } from "../settings/shared/settings-card"
import { DeleteOrganizationDialog } from "./delete-organization-dialog"

export function DeleteOrganizationCard({
    className,
    classNames,
    localization: localizationProp,
    slug,
    onSuccess,
    ...props
}: SettingsCardProps & { slug?: string; onSuccess?: () => Promise<void> }) {
    const { localization: contextLocalization } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const { data: organization } = useCurrentOrganization({ slug })

    if (!organization)
        return (
            <SettingsCard
                className={className}
                classNames={classNames}
                actionLabel={localization?.DELETE_ORGANIZATION}
                description={localization?.DELETE_ORGANIZATION_DESCRIPTION}
                isPending
                title={localization?.DELETE_ORGANIZATION}
                variant="destructive"
            />
        )

    return (
        <DeleteOrganizationForm
            className={className}
            classNames={classNames}
            localization={localization}
            organization={organization}
            onSuccess={onSuccess}
            {...props}
        />
    )
}

function DeleteOrganizationForm({
    className,
    classNames,
    localization: localizationProp,
    organization,
    onSuccess
}: SettingsCardProps & {
    organization: Organization
    onSuccess?: () => Promise<void>
}) {
    const {
        localization: contextLocalization,
        hooks: { useHasPermission }
    } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const { data: hasPermission, isPending } = useHasPermission({
        organizationId: organization.id,
        permissions: {
            organization: ["delete"]
        }
    })

    const [showDialog, setShowDialog] = useState(false)
    const actionButton = useRef<HTMLButtonElement>(null)

    if (!hasPermission?.success) return null

    return (
        <>
            <SettingsCard
                className={className}
                classNames={classNames}
                actionLabel={localization?.DELETE_ORGANIZATION}
                description={localization?.DELETE_ORGANIZATION_DESCRIPTION}
                isPending={isPending}
                title={localization?.DELETE_ORGANIZATION}
                variant="destructive"
                action={() => setShowDialog(true)}
                actionButtonRef={actionButton}
            />

            <DeleteOrganizationDialog
                classNames={classNames}
                localization={localization}
                open={showDialog}
                onOpenChange={setShowDialog}
                onCloseAutoFocus={(event) => { event.preventDefault(); actionButton.current?.focus() }}
                organization={organization}
                onSuccess={onSuccess}
            />
        </>
    )
}
